import { useCallback, useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useParams } from "react-router-dom";
import { API_URL } from "../api/client";
import Button from "../components/Button";
import LoadingSpinner from "../components/ui/LoadingSpinner";

function getDownloadFilename(response) {
  const disposition = response.headers.get("content-disposition") || "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  const filename = utf8Match?.[1] || filenameMatch?.[1];

  if (!filename) return "ebook.pdf";

  try {
    return decodeURIComponent(filename);
  } catch (error) {
    return filename;
  }
}

export default function DownloadRedirect() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [downloadAttempt, setDownloadAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let successTimer;

    async function downloadFile() {
      setStatus("loading");
      setError("");

      try {
        const response = await fetch(`${API_URL}/api/downloads/${token}`, {
          redirect: "manual",
          signal: controller.signal,
        });

        if (response.type === "opaqueredirect") {
          window.location.href = `${API_URL}/api/downloads/${token}`;
          successTimer = window.setTimeout(() => setStatus("success"), 1200);
          return;
        }

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.message || "The download could not be completed.");
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = objectUrl;
        link.download = getDownloadFilename(response);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        setStatus("success");
      } catch (downloadError) {
        if (downloadError.name === "AbortError") return;
        setError(downloadError.message || "The download could not be completed.");
        setStatus("error");
      }
    }

    downloadFile();
    return () => {
      controller.abort();
      window.clearTimeout(successTimer);
    };
  }, [token, downloadAttempt]);

  const retryDownload = useCallback(() => {
    setDownloadAttempt((attempt) => attempt + 1);
  }, []);

  return (
    <section className="section download-page">
      <div className="container">
        {status === "loading" && <LoadingSpinner label="Preparing secure download" />}

        {status === "error" && (
          <div className="download-status glass-card">
            <span className="eyebrow">Download failed</span>
            <h1>We could not download your file</h1>
            <p>{error}</p>
            <div className="ebook-actions">
              <Button onClick={retryDownload}>Try Again</Button>
              <Button to="/account" variant="secondary">Open Account</Button>
            </div>
          </div>
        )}

        {status === "success" && (
          <>
            <div className="download-status glass-card" aria-hidden="true">
              <h1>Your download is ready</h1>
            </div>
            <div className="download-modal-backdrop">
              <div
                className="download-modal glass-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="download-success-title"
                aria-describedby="download-success-message"
              >
                <div className="download-success-content">
                  <CheckCircle2 className="download-success-icon" aria-hidden="true" />
                  <span className="eyebrow">Download complete</span>
                  <h2 id="download-success-title">Downloaded successfully</h2>
                  <p id="download-success-message">
                    Your product has been downloaded. Check your browser downloads to open it.
                  </p>
                  <div className="ebook-actions">
                    <Button to="/account">Open Account</Button>
                    <Button to="/shop" variant="secondary">Back to Shop</Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
